import { MODEL_CATEGORY } from '@/app/constants/calculator';
import type {
  CompareItemFormatType,
  CompareItemType,
  LcvCompareModel,
} from '@/app/lcv/components/sections/compare/types';
import { formatNumber } from '@/app/utils/format';
import type { LcvModelCategory, LcvModelColor } from '@/types/tis-cms/types';

export const LCV_CATEGORY_TITLE_TO_PRODUCT_URL: Record<string, string> = {
  [MODEL_CATEGORY.PUP_V_CROSS]: '/isuzu-v-cross',
  [MODEL_CATEGORY.PUP_4_DOOR]: '/isuzu-pick-up-4-door',
  [MODEL_CATEGORY.PUP_2_DOOR]: '/isuzu-pick-up-2-door',
  [MODEL_CATEGORY.PUP_SPARK]: '/isuzu-spark',
  [MODEL_CATEGORY.PUP_X_SERIES]: '/isuzu-x-series',
  [MODEL_CATEGORY.PPV_MU_X]: '/isuzu-mu-x',
};

const NUMBER_FORMAT: Record<CompareItemFormatType, number> = {
  gear: 3,
  steering: 1,
};

const REGISTER_URL = '/register';

const PPV_MU_X_GRADE_PATHS = {
  rs: '',
  ultimate: '/ultimate',
  elegant: '/elegant',
  active: '/active',
} as const;

export const getCompareModelField = (
  model: LcvCompareModel | undefined,
  fieldKey: string
) => model?.[fieldKey as keyof LcvCompareModel];

export const parsedCompareValue = (value: unknown, item: CompareItemType) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'object' && value !== null) {
    const list: string[] = [];
    for (const key in value) {
      if (key.startsWith(`${item.fieldKey}_`)) {
        list.push((value as Record<string, string>)[key]);
      }
    }
    return list;
  }

  if (typeof value === 'string' && !!item.format) {
    return formatNumber(value, NUMBER_FORMAT[item.format]);
  }

  return value || 'ไม่มี';
};

export const getProductUrlFromCategoryTitle = (title?: string) =>
  title ? (LCV_CATEGORY_TITLE_TO_PRODUCT_URL[title] ?? '') : '';

export const getRegisterUrlFromLcvModelCategory = (
  category: LcvModelCategory
) => {
  if (category.title === MODEL_CATEGORY.PPV_MU_X) {
    return `${REGISTER_URL}?model=ppv`;
  }

  return REGISTER_URL;
};

export const getProductUrlFromLcvModel = (model: LcvCompareModel) => {
  const category = getLcvCategoryFromLcvModel(model);
  const categoryTitle = category?.title;
  const productUrl = getProductUrlFromCategoryTitle(categoryTitle);
  const titleSuffix = model.title.split(' : ').pop() ?? '';

  if (categoryTitle === MODEL_CATEGORY.PPV_MU_X) {
    const grade = Object.keys(PPV_MU_X_GRADE_PATHS).find((value) =>
      new RegExp(`\\b${value}\\b`, 'i').test(titleSuffix)
    ) as keyof typeof PPV_MU_X_GRADE_PATHS | undefined;

    return `${productUrl}${PPV_MU_X_GRADE_PATHS[grade ?? 'ultimate']}`;
  }

  if (
    categoryTitle === MODEL_CATEGORY.PUP_X_SERIES &&
    titleSuffix.includes('hi-lander')
  ) {
    return `${productUrl}/hi-lander`;
  }

  return productUrl;
};

export const getLcvModelColorId = (modelColor?: LcvModelColor | null) =>
  modelColor?.color?.title ?? '';

type LegacyContentstackCategory = {
  name_th?: string;
  name_en?: string;
  uid?: string;
};

type LegacyContentstackModel = {
  category?: LcvCompareModel['category'];
  lcv_model_class?: Array<{
    category?: Array<LegacyContentstackCategory>;
  }>;
};

export const getLcvCategoryFromLcvModel = (
  model: LcvCompareModel | LegacyContentstackModel
) => {
  if (model.category) {
    return model.category;
  }

  if (!('lcv_model_class' in model)) {
    return undefined;
  }

  const legacyCategory = model.lcv_model_class?.[0]?.category?.[0];
  if (!legacyCategory) {
    return undefined;
  }

  return {
    documentId: legacyCategory.uid ?? '',
    title: legacyCategory.name_th ?? '',
    nameTh: legacyCategory.name_th ?? '',
    nameEn: legacyCategory.name_en ?? '',
  } as LcvModelCategory;
};

export const getCategoryNameTh = (
  category: LcvModelCategory | LegacyContentstackCategory
) => ('nameTh' in category ? category.nameTh : category.name_th) ?? '';

export const createParsedKey = (model: { title: string }) =>
  `${model.title.split(' ')[0]}-${model.title.split(' : ').pop()}`;

export const createModelQueryParam = (modelId: string, colorId?: string) => {
  const normalizedModelId = modelId.toLowerCase();
  const normalizedColorId = colorId?.toLowerCase();
  return `${normalizedModelId}${normalizedColorId ? `_${normalizedColorId}` : ''}`;
};

export const getModelColors = (
  model?: LcvCompareModel | { colors?: Array<LcvModelColor | null | undefined> }
) => (model?.colors ?? []).filter((color): color is LcvModelColor => !!color);
